import React from 'react';
import { 
  Facebook, 
  Instagram, 
  Linkedin, 
  Mail, 
  MessageCircle,
  Download,
  Smartphone,
  MapPin,
  Clock,
  Phone,
  ArrowRight,
  Star,
  Shield,
  CreditCard,
  Globe,
  Users,
  TrendingUp,
  Calendar
} from 'lucide-react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Card, CardContent } from './ui/card';
import { AppleIcon, GooglePlayIcon } from './StoreIcons';

interface FooterProps {
  onNavigate?: (page: string) => void;
}

export function Footer({ onNavigate }: FooterProps) {
  const quickLinks = [
    { label: 'Como Comprar', href: 'ajuda' },
    { label: 'Central de Ajuda', href: 'ajuda' },
    { label: 'Reembolsos', href: 'ajuda' },
    { label: 'Para Organizadores', href: 'produtor' }
  ];

  const legalLinks = [
    { label: 'Termos de Uso', href: 'https://uniqueingressos.com.br/uploads/termo_uso/termos_e_condicoes.pdf', external: true },
    { label: 'Política de Privacidade', href: 'politica-privacidade', external: false },
    { label: 'Política de Cookies', href: 'politica-cookies', external: false }
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/uniqueingressos/', name: 'Instagram', color: 'hover:bg-pink-500' },
    { icon: Facebook, href: 'https://www.facebook.com/uniqueingressos', name: 'Facebook', color: 'hover:bg-blue-600' },
    { icon: Linkedin, href: 'https://www.linkedin.com/company/unique-ingressos/', name: 'LinkedIn', color: 'hover:bg-blue-700' }
  ];

  const trustFeatures = [
    { icon: Shield, text: 'Compra 100% Segura' },
    { icon: CreditCard, text: 'Pagamento Protegido' },
    { icon: Star, text: '4.8★ Avaliação' },
    { icon: Globe, text: 'Suporte 24/7' }
  ];

  const handleCreateEventsClick = () => {
    if (onNavigate) {
      onNavigate('produtor');
    }
  };

  return (
    <footer className="relative bg-gradient-to-b from-background via-muted/20 to-black-deep text-foreground overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-primary/10 via-transparent to-orange-primary/5"></div>
        <div className="absolute top-0 left-0 w-72 h-72 bg-orange-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-primary/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      </div>

      {/* Para Produtores Section */}
      <div className="relative bg-gradient-to-r from-slate-900 via-black-deep to-slate-900 py-16">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-primary/5 via-orange-primary/10 to-orange-primary/5"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center mb-8">
              <div className="flex items-center space-x-3 bg-orange-primary/10 backdrop-blur-sm border border-orange-primary/20 rounded-full px-6 py-3">
                <Users className="w-6 h-6 text-orange-primary" />
                <span className="text-orange-primary font-medium">Para Produtores</span>
              </div>
            </div>
            
            <div className="space-y-6 mb-10">
              <p className="text-white text-xl lg:text-2xl font-medium leading-relaxed">
                Para produtoras que buscam eficiência e inteligência.
              </p>
              
              <p className="text-white/80 text-lg lg:text-xl leading-relaxed">
                Tecnologia integrada para vendas físicas, online e gestão de dados.
              </p>
            </div>
            
            <Button
              size="lg"
              onClick={handleCreateEventsClick}
              className="bg-gradient-to-r from-orange-primary to-orange-primary/80 hover:from-orange-primary/90 hover:to-orange-primary/70 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-2xl border-0 transition-all duration-300 hover:scale-105 hover:shadow-orange-primary/25"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Criar evento
            </Button>
          </div>
        </div>
      </div>

      {/* App Download Banner */}
      <div className="relative bg-gradient-to-r from-orange-primary via-orange-primary to-orange-primary/90 py-16">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 relative">
          <div className="flex flex-col lg:flex-row items-center justify-between max-w-6xl mx-auto">
            <div className="text-center lg:text-left mb-8 lg:mb-0 lg:flex-1">
              <h3 className="text-2xl lg:text-3xl font-display mb-3 text-white">
                Leve a Unique no seu bolso
              </h3>
              <p className="text-white/90 text-lg max-w-lg">
                Baixe nosso app e tenha acesso a ofertas exclusivas, notificações em tempo real e uma experiência ainda melhor.
              </p>

            </div>
            
            <div className="app-store-buttons">
              <Button
                size="lg"
                variant="secondary"
                className="app-store-button bg-white/95 text-black hover:bg-white hover:scale-105 transition-all duration-300 shadow-lg"
                onClick={() => window.open('https://apps.apple.com/br/app/unique-ingressos/id1667632306', '_blank')}
              >
                <AppleIcon size={24} className="app-store-icon" />
                <div className="app-store-text">
                  <div className="app-store-text-line app-store-text-small">Download na</div>
                  <div className="app-store-text-line app-store-text-large">App Store</div>
                </div>
              </Button>
              
              <Button
                size="lg"
                variant="secondary"
                className="app-store-button bg-white/95 text-black hover:bg-white hover:scale-105 transition-all duration-300 shadow-lg"
                onClick={() => window.open('https://play.google.com/store/apps/details?id=br.com.uniqueingressos.unique_ingressos_app', '_blank')}
              >
                <GooglePlayIcon size={24} className="app-store-icon" />
                <div className="app-store-text">
                  <div className="app-store-text-line app-store-text-small">Disponível no</div>
                  <div className="app-store-text-line app-store-text-large">Google Play</div>
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="relative">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            
            {/* Company Info - Takes more space */}
            <div className="lg:col-span-5">
              <Card className="bg-card/50 backdrop-blur-sm border-border/50 h-full">
                <CardContent className="p-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <img 
                      src="https://app.uniqueingressos.com.br/images/unique-logo.svg" 
                      alt="Unique Ingressos" 
                      className="h-12 w-auto dark:filter dark:brightness-0 dark:invert"
                    />
                    <div>
                      <h3 className="font-display text-xl">
                        Unique <span className="text-orange-primary">Ingressos</span>
                      </h3>
                      <p className="text-muted-foreground text-sm">Tecnologia que impulsiona as maiores produtoras</p>
                    </div>
                  </div>
                  
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    Mais do que uma plataforma de venda de ingressos, somos a solução inteligente para grandes eventos.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Links */}
            <div className="lg:col-span-3">
              <Card className="bg-card/50 backdrop-blur-sm border-border/50 h-full">
                <CardContent className="p-8">
                  <h4 className="font-heading text-lg mb-6 flex items-center">
                    <span className="w-1 h-6 bg-orange-primary rounded-full mr-3"></span>
                    Links Rápidos
                  </h4>
                  <ul className="space-y-3">
                    {quickLinks.map((link, index) => (
                      <li key={index}>
                        <button
                          onClick={() => onNavigate && onNavigate(link.href)}
                          className="flex items-center space-x-2 text-muted-foreground hover:text-orange-primary transition-all duration-200 group cursor-pointer"
                        >
                          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                          <span>{link.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Legal Links */}
            <div className="lg:col-span-4">
              <Card className="bg-card/50 backdrop-blur-sm border-border/50 h-full">
                <CardContent className="p-8">
                  <h4 className="font-heading text-lg mb-6 flex items-center">
                    <span className="w-1 h-6 bg-orange-primary rounded-full mr-3"></span>
                    Políticas & Legal
                  </h4>
                  <ul className="space-y-3">
                    {legalLinks.map((link, index) => (
                      <li key={index}>
                        {link.external ? (
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 text-muted-foreground hover:text-orange-primary transition-all duration-200 group"
                          >
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                            <span>{link.label}</span>
                          </a>
                        ) : (
                          <button
                            onClick={() => onNavigate && onNavigate(link.href)}
                            className="flex items-center space-x-2 text-muted-foreground hover:text-orange-primary transition-all duration-200 group cursor-pointer"
                          >
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                            <span>{link.label}</span>
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Social Media & Support Section */}
          <Card className="bg-card/50 backdrop-blur-sm border-border/50 mb-8">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* Social Media */}
                <div className="text-center lg:text-left">
                  <h4 className="font-heading text-lg mb-6 flex items-center justify-center lg:justify-start">
                    <span className="w-1 h-6 bg-orange-primary rounded-full mr-3"></span>
                    Siga-nos nas Redes
                  </h4>
                  
                  <div className="flex justify-center lg:justify-start space-x-4 mb-6">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-12 h-12 bg-muted/50 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 hover:-translate-y-1 ${social.color} group`}
                        aria-label={social.name}
                      >
                        <social.icon size={20} className="group-hover:text-white transition-colors" />
                      </a>
                    ))}
                  </div>
                  
                  <p className="text-muted-foreground text-sm text-center lg:text-left">
                    Acompanhe nossas novidades, eventos exclusivos e promoções especiais.
                  </p>

                 {/* Bottom Footer */}
                <div className="flex flex-col lg:flex-row justify-between items-center space-y-6 lg:space-y-0 text-sm text-muted-foreground">
                  <div className="text-center">
                    <p className="mb-1">&copy; {new Date().getFullYear()} Unique Ingressos. Todos os direitos reservados.</p>
                    <p className="text-xs">
                      CNPJ: 39.776.762/0001-10 • Unique Pay Meios de Pagamentos LTDA
                    </p>
                  </div>
                </div>
                </div>

                {/* WhatsApp Support */}
                <div className="text-center lg:text-right">
                  <h4 className="font-heading text-lg mb-6 flex items-center justify-center lg:justify-end">
                    <span className="w-1 h-6 bg-orange-primary rounded-full mr-3"></span>
                    Suporte Direto
                  </h4>
                  
                  <div className="flex justify-center lg:justify-end">
                    <Button
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 text-white border-0 mb-4 transition-all duration-300 hover:scale-105 hover:shadow-lg"
                      onClick={() => window.open('https://api.whatsapp.com/send?l=pt&phone=5582996064988', '_blank')}
                    >
                      <MessageCircle size={20} className="mr-2" />
                      Chamar no WhatsApp
                    </Button>
                  </div>

                </div>
              </div>
            </CardContent>
          </Card>

          <Separator className="mb-8 bg-border/50" />


        </div>
      </div>
    </footer>
  );
}